/**
 * TimingDefenseGate — S-CP10-4
 *
 * Full-screen overlay that fires when a rep enters or changes the Close Date
 * field at CP10. Asks Q10.TIME.1–4, computes the system band, surfaces the
 * six-button declared-timing selector, and routes over-call / under-call /
 * within-band paths.
 *
 * Brand: Navy #0B0B0F · Red #DC2626 · Off-white #FAFAF7
 * Roles: bdr, ae, manager, executive, marketing — never "rep" in user-facing copy
 */

import { useState, useMemo } from "react";
import type {
  DeclaredTimingButton,
  SoftStopFlag,
  TimingAnswer,
  TimingBand,
  TimingCustomerConfirmed,
  TimingPaperRunway,
  TimingSlipHistory,
  TimingTriggerEvent,
} from "@/lib/cp10-timing-band";
import {
  buttonToPct,
  computeTimingBand,
  detectOverUnderCall,
  SOFT_STOP_FLAG_LABEL,
} from "@/lib/cp10-timing-band";
import {
  buildForecastDisplay,
  computeBlendedForecast,
  getCategoryEligibility,
} from "@/lib/cp10-rule-forecast-1";
import {
  saveTimingAssessment,
  sendManagerSoftStopFlag,
  writeTimingAuditLog,
} from "@/lib/cp10-timing-store";
import { getSession } from "@/lib/session";

const BLACK = "#0B0B0F";
const RED = "#DC2626";
const BG = "#FAFAF7";
const AMBER = "#CA8A04";
const MUTED = "#8E8E94";

interface TimingDefenseGateProps {
  open: boolean;
  dealId: string;
  accountId: number; // bigint id of the parent account — required for RULE-FORECAST-1 join
  closeDate: string; // ISO yyyy-mm-dd
  compositePct: number; // 0–100, the M-PACT composite at time-of-gate
  priorAnswers?: Partial<TimingAnswer>;
  priorSlipCount?: number; // pre-populate informational copy on Q4
  onClose: () => void;
  onComplete: (result: {
    declaredButton: DeclaredTimingButton;
    band: TimingBand;
    blendedForecastPct: number;
  }) => void;
}

const Q1_OPTIONS: { value: TimingTriggerEvent; label: string }[] = [
  { value: "budget_cycle_end", label: "Budget cycle end" },
  { value: "contract_license_expiry", label: "Contract/license expiry" },
  { value: "fiscal_year_end", label: "Fiscal year end" },
  { value: "project_kickoff_dependency", label: "Project kickoff dependency" },
  { value: "regulatory_compliance_deadline", label: "Regulatory/compliance deadline" },
  { value: "executive_mandate_with_date", label: "Executive mandate with date" },
  { value: "none_of_these", label: "None of these" },
];
const Q2_OPTIONS: { value: TimingCustomerConfirmed; label: string }[] = [
  { value: "signed_written_commitment", label: "Signed/written commitment" },
  { value: "verbal_from_economic_buyer", label: "Verbal from economic buyer" },
  { value: "verbal_from_champion_only", label: "Verbal from champion only" },
  { value: "rep_inference_only", label: "My own inference only" },
];
const Q3_OPTIONS: { value: TimingPaperRunway; label: string }[] = [
  { value: "yes_validated_with_procurement", label: "Yes — validated with procurement contact" },
  { value: "yes_rep_estimated", label: "Yes — I estimated based on past deals" },
  { value: "unknown_no_procurement_contact", label: "Unknown — no procurement contact yet" },
  { value: "no_runway_tight", label: "No — runway is tight" },
];
const Q4_OPTIONS: { value: TimingSlipHistory; label: string }[] = [
  { value: "never", label: "Never" },
  { value: "once", label: "Once" },
  { value: "twice", label: "Twice" },
  { value: "three_or_more", label: "Three or more times" },
];

const BUTTONS: { value: DeclaredTimingButton; label: string }[] = [
  { value: "90", label: "90%" },
  { value: "80", label: "80%" },
  { value: "70", label: "70%" },
  { value: "60", label: "60%" },
  { value: "50", label: "50%" },
  { value: "below_50", label: "Below 50" },
];

function formatCloseDate(iso: string): string {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return iso;
  }
}

function isWithinBand(button: DeclaredTimingButton, band: TimingBand): boolean {
  const pct = buttonToPct(button);
  return pct >= band.lower && pct <= band.upper;
}

export function TimingDefenseGate(props: TimingDefenseGateProps) {
  const { open, dealId, accountId, closeDate, compositePct, priorAnswers, priorSlipCount, onClose, onComplete } = props;

  const [q1, setQ1] = useState<TimingTriggerEvent | null>(priorAnswers?.q1_trigger_event ?? null);
  const [q2, setQ2] = useState<TimingCustomerConfirmed | null>(priorAnswers?.q2_customer_confirmed ?? null);
  const [q3, setQ3] = useState<TimingPaperRunway | null>(priorAnswers?.q3_paper_runway ?? null);
  const [q4, setQ4] = useState<TimingSlipHistory | null>(priorAnswers?.q4_slip_history ?? null);
  const [provisionalButton, setProvisionalButton] = useState<DeclaredTimingButton | null>(null);
  const [confirmedButton, setConfirmedButton] = useState<DeclaredTimingButton | null>(null);
  const [justification, setJustification] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showOvercallModal, setShowOvercallModal] = useState(false);
  const [showUndercallConfirm, setShowUndercallConfirm] = useState(false);

  const allAnswered = q1 && q2 && q3 && q4;
  const band: TimingBand | null = useMemo(() => {
    if (!allAnswered) return null;
    return computeTimingBand({
      q1_trigger_event: q1!,
      q2_customer_confirmed: q2!,
      q3_paper_runway: q3!,
      q4_slip_history: q4!,
    });
  }, [q1, q2, q3, q4, allAnswered]);

  if (!open) return null;

  function handleButtonClick(b: DeclaredTimingButton) {
    if (!band) return;
    setProvisionalButton(b);
    setConfirmedButton(null);
    const result = detectOverUnderCall(b, band);
    if (result.type === "over_call") {
      setShowOvercallModal(true);
      setShowUndercallConfirm(false);
    } else if (result.type === "under_call") {
      setShowUndercallConfirm(true);
      setShowOvercallModal(false);
    } else {
      setShowOvercallModal(false);
      setShowUndercallConfirm(false);
      void finalize(b, null, false, false);
    }
  }

  async function finalize(
    button: DeclaredTimingButton,
    overcallJustification: string | null,
    overcallFlagSent: boolean,
    undercallConfirmed: boolean,
  ) {
    if (!band || !allAnswered) return;
    setSubmitting(true);
    try {
      const declared_pct = buttonToPct(button);
      const blended = computeBlendedForecast(compositePct, button);
      const answers: TimingAnswer = {
        q1_trigger_event: q1!,
        q2_customer_confirmed: q2!,
        q3_paper_runway: q3!,
        q4_slip_history: q4!,
      };

      await saveTimingAssessment({
        deal_id: dealId,
        account_id: accountId,
        close_date: closeDate,
        answers,
        band,
        declared_button: button,
        declared_pct,
        overcall_justification: overcallJustification,
        overcall_flag_sent: overcallFlagSent,
        undercall_confirmed: undercallConfirmed,
        blended_forecast_pct: blended,
      });

      await writeTimingAuditLog("TIMING_GATE_COMPLETED", dealId, {
        rep_declared_pct: declared_pct,
        system_band_lower: band.lower,
        system_band_upper: band.upper,
        close_date: closeDate,
      });

      if (overcallFlagSent && overcallJustification) {
        const session = getSession();
        await sendManagerSoftStopFlag({
          deal_id: dealId,
          flag_type: "TIMING_OVERCALL",
          rep_declared_pct: declared_pct,
          system_band_lower: band.lower,
          system_band_upper: band.upper,
          justification_text: overcallJustification,
          close_date: closeDate,
          rep_id: session?.id ?? "unknown",
        });
        await writeTimingAuditLog("TIMING_OVERCALL_JUSTIFICATION_SUBMITTED", dealId, {
          rep_declared_pct: declared_pct,
          system_band_lower: band.lower,
          system_band_upper: band.upper,
          justification_text: overcallJustification,
          close_date: closeDate,
        });
      }
      if (undercallConfirmed) {
        await writeTimingAuditLog("TIMING_UNDERCALL_CONFIRMED", dealId, {
          rep_declared_pct: declared_pct,
          system_band_lower: band.lower,
          system_band_upper: band.upper,
          delta: band.lower - declared_pct,
          close_date: closeDate,
        });
      }

      setConfirmedButton(button);
      onComplete({ declaredButton: button, band, blendedForecastPct: blended });
    } finally {
      setSubmitting(false);
    }
  }

  function handleOvercallSubmit() {
    if (!provisionalButton || !band) return;
    if (justification.trim().length < 80) return;
    setShowOvercallModal(false);
    void finalize(provisionalButton, justification.trim(), true, false);
  }

  function handleOvercallCancel() {
    setShowOvercallModal(false);
    setProvisionalButton(null);
    setJustification("");
  }

  function handleUndercallConfirm() {
    if (!provisionalButton) return;
    setShowUndercallConfirm(false);
    void finalize(provisionalButton, null, false, true);
  }

  function handleUndercallCancel() {
    setShowUndercallConfirm(false);
    setProvisionalButton(null);
  }

  // ─── Render ──────────────────────────────────────────────────────────────
  const closeDateLabel = formatCloseDate(closeDate);
  const slipBanner =
    priorSlipCount && priorSlipCount > 0
      ? `Our records show this deal's close date has been changed ${priorSlipCount} time(s). Please select below.`
      : null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(11,11,15,0.55)",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        zIndex: 1000,
        overflowY: "auto",
        padding: "32px 16px",
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="timing-gate-title"
    >
      <div
        style={{
          width: "100%",
          maxWidth: "720px",
          background: BG,
          color: BLACK,
          borderRadius: "10px",
          boxShadow: "0 10px 40px rgba(0,0,0,0.25)",
          padding: "28px 32px",
          fontFamily: "ui-sans-serif, system-ui, -apple-system, sans-serif",
        }}
      >
        <h2 id="timing-gate-title" style={{ fontSize: "20px", fontWeight: 700, marginBottom: "6px" }}>
          Timing assessment — Close date: {closeDateLabel}
        </h2>
        <p style={{ fontSize: "13px", color: MUTED, marginBottom: "20px" }}>
          Your close date was just set. Answer four quick questions to confirm your timing confidence. About 60 seconds.
        </p>

        {/* Q1 */}
        <Question
          number={1}
          dataTestid="q10-time-1"
          legend={`What specific event on or before ${closeDateLabel} forces the customer to sign by then?`}
          options={Q1_OPTIONS}
          value={q1}
          onChange={(v) => {
            setQ1(v);
            setProvisionalButton(null);
            setConfirmedButton(null);
          }}
        />

        {/* Q2 — revealed after Q1 */}
        {q1 && (
          <Question
            number={2}
            dataTestid="q10-time-2"
            legend="Has the customer verbally or in writing committed to that close date?"
            options={Q2_OPTIONS}
            value={q2}
            onChange={(v) => {
              setQ2(v);
              setProvisionalButton(null);
              setConfirmedButton(null);
            }}
          />
        )}

        {/* Q3 — revealed after Q2 */}
        {q1 && q2 && (
          <Question
            number={3}
            dataTestid="q10-time-3"
            legend="Given this customer's known procurement, legal, security, and technical review time, does the close date leave enough runway?"
            options={Q3_OPTIONS}
            value={q3}
            onChange={(v) => {
              setQ3(v);
              setProvisionalButton(null);
              setConfirmedButton(null);
            }}
          />
        )}

        {/* Q4 — revealed after Q3 */}
        {q1 && q2 && q3 && (
          <Question
            number={4}
            dataTestid="q10-time-4"
            legend="Has this deal's close date moved before? How many times?"
            informational={slipBanner}
            options={Q4_OPTIONS}
            value={q4}
            onChange={(v) => {
              setQ4(v);
              setProvisionalButton(null);
              setConfirmedButton(null);
            }}
          />
        )}

        {/* Band display + button row */}
        {band && (
          <div style={{ marginTop: "28px" }}>
            <div
              aria-live="polite"
              style={{
                background: "#fff",
                border: `1px solid ${BLACK}1A`,
                borderRadius: "8px",
                padding: "16px 20px",
              }}
              data-testid="timing-system-band"
            >
              <div style={{ fontSize: "12px", color: MUTED, marginBottom: "4px" }}>
                Based on your answers, the system recommends:
              </div>
              <div style={{ fontSize: "28px", fontWeight: 700, letterSpacing: "-0.5px" }}>
                {band.lower}–{band.upper}%
              </div>
              {band.soft_stop_flags.length > 0 && (
                <ul
                  style={{
                    marginTop: "12px",
                    padding: "10px 14px",
                    background: `${AMBER}14`,
                    borderLeft: `3px solid ${AMBER}`,
                    listStyle: "none",
                    fontSize: "12px",
                  }}
                  data-testid="timing-soft-stop-flags"
                >
                  {band.soft_stop_flags.map((f) => (
                    <li key={f}>• {SOFT_STOP_FLAG_LABEL[f as SoftStopFlag]}</li>
                  ))}
                </ul>
              )}
            </div>

            <div style={{ marginTop: "20px", fontSize: "13px", fontWeight: 600 }}>
              How confident are you that this deal will close by {closeDateLabel}?
            </div>

            <div
              role="group"
              aria-label="Declared timing confidence"
              style={{
                marginTop: "12px",
                display: "grid",
                gridTemplateColumns: "repeat(6, minmax(0,1fr))",
                gap: "8px",
              }}
              data-testid="timing-button-row"
            >
              {BUTTONS.map((b) => {
                const within = isWithinBand(b.value, band);
                const pct = buttonToPct(b.value);
                const isOver = pct > band.upper;
                const isUnder = pct < band.lower;
                const selected = provisionalButton === b.value || confirmedButton === b.value;
                const bg = selected
                  ? isOver
                    ? AMBER
                    : isUnder
                    ? MUTED
                    : BLACK
                  : "#fff";
                const fg = selected ? "#fff" : BLACK;
                return (
                  <button
                    key={b.value}
                    type="button"
                    aria-pressed={selected}
                    aria-label={`Declare timing confidence: ${b.value === "below_50" ? "below 50 percent" : b.label}`}
                    data-testid={`timing-button-${b.value}`}
                    onClick={() => handleButtonClick(b.value)}
                    disabled={submitting}
                    style={{
                      padding: "12px 8px",
                      borderRadius: "8px",
                      border: within ? `2px solid ${BLACK}` : `1px solid ${BLACK}33`,
                      background: bg,
                      color: fg,
                      fontWeight: 600,
                      fontSize: "13px",
                      cursor: submitting ? "not-allowed" : "pointer",
                    }}
                  >
                    {b.label}
                  </button>
                );
              })}
            </div>

            <div style={{ marginTop: "10px", fontSize: "11px", color: MUTED, lineHeight: 1.5 }}>
              <strong>What does this mean?</strong> Your declared timing % sets which forecast categories this deal is eligible for.
              <br />
              90%+ → Commit eligible · 80–70% → Best Case max · 60% → Pipeline only · 50% → Pipeline with manager flag · Below 50% → Push or Loss only.
            </div>
          </div>
        )}

        {/* Under-call inline confirmation */}
        {showUndercallConfirm && provisionalButton && band && (
          <div
            style={{
              marginTop: "16px",
              padding: "16px 20px",
              background: "#fff",
              border: `1px solid ${MUTED}`,
              borderRadius: "8px",
            }}
            data-testid="timing-undercall-confirm"
          >
            <div style={{ fontSize: "13px", marginBottom: "8px" }}>
              You declared {provisionalButton === "below_50" ? "Below 50" : `${provisionalButton}%`}. Your answers support {band.lower}–{band.upper}%.
              Are you sure you want to go lower?
            </div>
            <div style={{ display: "flex", gap: "10px", marginTop: "12px" }}>
              <button
                type="button"
                onClick={handleUndercallCancel}
                disabled={submitting}
                style={{
                  padding: "8px 14px",
                  borderRadius: "6px",
                  border: `1px solid ${BLACK}33`,
                  background: "#fff",
                  fontSize: "12px",
                  cursor: "pointer",
                }}
              >
                No, change selection
              </button>
              <button
                type="button"
                onClick={handleUndercallConfirm}
                disabled={submitting}
                style={{
                  padding: "8px 14px",
                  borderRadius: "6px",
                  border: "none",
                  background: BLACK,
                  color: "#fff",
                  fontSize: "12px",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
                data-testid="timing-undercall-yes"
              >
                Yes, confirm {provisionalButton === "below_50" ? "Below 50" : `${provisionalButton}%`}
              </button>
            </div>
          </div>
        )}

        {/* Confirmation summary after a finalize */}
        {confirmedButton && band && (
          <ConfirmationSummary
            closeDate={closeDateLabel}
            declaredButton={confirmedButton}
            band={band}
            compositePct={compositePct}
            onClose={onClose}
          />
        )}

        {/* Footer */}
        <div style={{ marginTop: "24px", display: "flex", justifyContent: "flex-end", gap: "10px" }}>
          {!confirmedButton && (
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: "8px 16px",
                borderRadius: "6px",
                background: "transparent",
                border: `1px solid ${BLACK}33`,
                color: BLACK,
                fontSize: "12px",
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
          )}
        </div>
      </div>

      {/* Over-call modal */}
      {showOvercallModal && provisionalButton && band && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="overcall-title"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(11,11,15,0.65)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1100,
            padding: "16px",
          }}
          data-testid="timing-overcall-modal"
        >
          <div
            style={{
              width: "100%",
              maxWidth: "560px",
              background: BG,
              color: BLACK,
              borderRadius: "10px",
              padding: "24px 28px",
              boxShadow: "0 10px 40px rgba(0,0,0,0.3)",
            }}
          >
            <h3 id="overcall-title" style={{ fontSize: "16px", fontWeight: 700, color: RED, marginBottom: "12px" }}>
              Your declared timing is higher than your answers support
            </h3>
            <p style={{ fontSize: "13px", lineHeight: 1.5, marginBottom: "12px" }}>
              You declared <strong>{provisionalButton === "below_50" ? "Below 50" : `${provisionalButton}%`}</strong>.<br />
              Your answers support <strong>{band.lower}–{band.upper}%</strong>.
            </p>
            <p style={{ fontSize: "13px", lineHeight: 1.5, marginBottom: "10px" }}>
              Please explain why you are confident this deal will close by {closeDateLabel} despite the gap.
            </p>
            <label htmlFor="overcall-justification" style={{ fontSize: "11px", color: MUTED, display: "block", marginBottom: "4px" }}>
              Your justification (required)
            </label>
            <textarea
              id="overcall-justification"
              value={justification}
              onChange={(e) => setJustification(e.target.value)}
              placeholder="Describe the specific reason you are confident in this close date despite the system's assessment."
              maxLength={1000}
              rows={5}
              data-testid="timing-overcall-textarea"
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: "6px",
                border: `1px solid ${BLACK}33`,
                fontSize: "13px",
                fontFamily: "inherit",
                resize: "vertical",
              }}
            />
            <div
              style={{
                marginTop: "6px",
                fontSize: "11px",
                color: justification.trim().length >= 80 ? "#16A34A" : MUTED,
              }}
              data-testid="timing-overcall-counter"
            >
              {justification.trim().length} / 80 characters minimum
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "18px" }}>
              <button
                type="button"
                onClick={handleOvercallCancel}
                style={{
                  padding: "8px 14px",
                  borderRadius: "6px",
                  border: `1px solid ${BLACK}33`,
                  background: "#fff",
                  fontSize: "12px",
                  cursor: "pointer",
                }}
              >
                Cancel — change my selection
              </button>
              <button
                type="button"
                onClick={handleOvercallSubmit}
                disabled={justification.trim().length < 80 || submitting}
                data-testid="timing-overcall-submit"
                style={{
                  padding: "8px 14px",
                  borderRadius: "6px",
                  border: "none",
                  background: justification.trim().length >= 80 ? BLACK : `${BLACK}55`,
                  color: "#fff",
                  fontSize: "12px",
                  fontWeight: 600,
                  cursor: justification.trim().length >= 80 ? "pointer" : "not-allowed",
                }}
              >
                Submit justification
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

interface QuestionProps<T extends string> {
  number: number;
  legend: string;
  options: { value: T; label: string }[];
  value: T | null;
  onChange: (v: T) => void;
  informational?: string | null;
  dataTestid?: string;
}

function Question<T extends string>({ number, legend, options, value, onChange, informational, dataTestid }: QuestionProps<T>) {
  const BLACK = "#0B0B0F";
  return (
    <fieldset
      style={{
        marginTop: "20px",
        border: "none",
        padding: 0,
      }}
      data-testid={dataTestid}
    >
      <legend style={{ fontSize: "13px", fontWeight: 600, color: BLACK, marginBottom: "8px" }}>
        {number}. {legend}
      </legend>
      {informational && (
        <div
          style={{
            fontSize: "11px",
            color: "#8E8E94",
            background: "#fff",
            padding: "8px 12px",
            borderRadius: "6px",
            border: "1px solid #E5E5E5",
            marginBottom: "8px",
          }}
        >
          {informational}
        </div>
      )}
      <div style={{ display: "grid", gap: "6px" }}>
        {options.map((o) => {
          const selected = value === o.value;
          return (
            <label
              key={o.value}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                padding: "10px 12px",
                background: selected ? "#fff" : "transparent",
                border: selected ? `1px solid ${BLACK}` : "1px solid #E5E5E5",
                borderRadius: "6px",
                fontSize: "13px",
                cursor: "pointer",
              }}
              data-testid={`${dataTestid}-option-${o.value}`}
            >
              <input
                type="radio"
                checked={selected}
                onChange={() => onChange(o.value)}
                style={{ accentColor: BLACK }}
              />
              <span>{o.label}</span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}

interface ConfirmationSummaryProps {
  closeDate: string;
  declaredButton: DeclaredTimingButton;
  band: TimingBand;
  compositePct: number;
  onClose: () => void;
}

function ConfirmationSummary({ closeDate, declaredButton, band, compositePct, onClose }: ConfirmationSummaryProps) {
  const display = buildForecastDisplay(compositePct, declaredButton, band.lower, band.upper);
  const elig = getCategoryEligibility(declaredButton);
  const BLACK = "#0B0B0F";
  return (
    <div
      style={{
        marginTop: "20px",
        background: "#fff",
        border: `1px solid ${BLACK}`,
        borderRadius: "8px",
        padding: "16px 20px",
      }}
      data-testid="timing-confirmation"
    >
      <div style={{ fontSize: "14px", fontWeight: 700, marginBottom: "8px" }}>Timing assessment complete</div>
      <div style={{ fontSize: "12px", lineHeight: 1.7 }}>
        <div>Close date: <strong>{closeDate}</strong></div>
        <div>Your timing: <strong>{declaredButton === "below_50" ? "Below 50" : `${declaredButton}%`}</strong></div>
        <div>Blended forecast: <strong>{display.blended_forecast_pct}%</strong> ({compositePct}% composite × {display.timing_pct}% timing)</div>
      </div>
      <div style={{ fontSize: "11px", color: "#8E8E94", marginTop: "10px" }}>
        Category eligibility:{" "}
        {elig.commit_eligible && "Commit · "}
        {elig.best_case_eligible && "Best Case · "}
        {elig.pipeline_eligible && "Pipeline"}
        {elig.push_or_loss_only && "Push or Loss only"}
      </div>
      {display.below_band_reason && (
        <div style={{ fontSize: "11px", color: "#CA8A04", marginTop: "8px" }}>{display.below_band_reason}</div>
      )}
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "14px" }}>
        <button
          type="button"
          onClick={onClose}
          style={{
            padding: "8px 16px",
            borderRadius: "6px",
            border: "none",
            background: BLACK,
            color: "#fff",
            fontSize: "12px",
            fontWeight: 600,
            cursor: "pointer",
          }}
          data-testid="timing-confirmation-close"
        >
          Close and return to CP10
        </button>
      </div>
    </div>
  );
}
